import { History } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta, QueryDefinition } from '@reduxjs/toolkit/dist/query';
import { UseQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { t } from 'i18next';
import { useState } from 'react';
import ApprovalStatusChip from '../../components/ApprovalStatusChip';
import { CriticalityScoreBadge } from '../../components/Badges/CriticalityScoreBadge';
import DatagridWrapper, { DataGridId } from '../../components/DatagridWrapper';
import InfoButton from '../../components/InfoButton';
import InfoDrawer from '../../components/InfoDrawer';
import { DateToFormattedString } from '../../components/Logic/DateLogic';
import { OpenInNewTabButton } from '../../components/OpenInNewTabButton';
import getEnvironment from '../../env';
import { AgreementResponse } from '../../models/Agreement';
import { PaginationQueryPackage } from '../../models/API/QueryParams/PaginationQueryPackage';
import Client from '../../models/Client';
import { ApprovalStatus } from '../../models/enums/ApprovalStatus';
import { CriticalityEnum } from '../../models/enums/CriticalityEnum';
import { ServiceBase, ServiceResponse } from '../../models/Service';
import { GetManyPackage } from '../../redux/GetManyPackage';
import { PermissionEnum } from '../Permission/PermissionEnum';
import { Can } from '../UserApi/logic/Can';
import { AgreementInfoDialog } from './agreementInfoDialog/AgreementInfoDialog';
import { compareIpList } from '../../components/Logic/IPComparator';

function getCurrentAgreement(agreements: AgreementResponse[], params: any) {
    return agreements.find(x => x.uuid === params.id)!;
}

class AgreementListProps {
    getAgreements?: UseQuery<QueryDefinition<GetManyPackage, BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, object, FetchBaseQueryMeta>, any, AgreementResponse[], any>>
    idToFetch?: string
    statusList: ApprovalStatus[] = []
    agreements?: AgreementResponse[]
    defaultPagesize?: number
    simpleView?: boolean = false;
    dataGridId?: DataGridId
}


export const AgreementList = (props: AgreementListProps) => {
    return (<AgreementListContent {...props} />)
}

const AgreementListContent = (props: AgreementListProps) => {

    const [clickedRow, setClickedRow] = useState<AgreementResponse | undefined>(undefined)

    const getColumns = (agreements: AgreementResponse[]) => {


        const simpleView1: GridColDef[] = [
            { field: 'short_id', sortable: true, type: "number", headerName: t('ID'), renderCell: (params: { row: AgreementResponse }) => <div>{params.row.short_id}</div>, flex: 2 },
            { field: 'description', sortable: true, type: "string", headerName: t('Description'), minWidth: 150, flex: 5 },
            { field: 'criticality', sortable: true, type: "singleSelect", valueOptions: Object.values(CriticalityEnum).map(e => t(e) + ""), headerName: t('Criticality'), valueGetter: (criticality) => t(criticality + ""), minWidth: 80, flex: 3 },
            { field: 'service', sortable: true, type: "string", headerName: t('Service'), valueGetter: (value: ServiceResponse) => value.name, minWidth: 200, flex: 4 },
        ];
        const simpleView2: GridColDef[] = [
            { field: 'client', sortable: true, type: "string", headerName: t('Client'), valueGetter: (client: Client) => client.name, flex: 4 },
        ];

        const advancedView1: GridColDef[] = [
            { field: 'service_organisation', sortable: true, type: "string", headerName: t('Service organisation'), valueGetter: (_, row: AgreementResponse) => row.service?.owner_organisation?.name, flex: 4 },
        ];
        const advancedView2: GridColDef[] = [
            { field: 'client_organisation', sortable: true, type: "string", headerName: t('Client organisation'), valueGetter: (_, row: AgreementResponse) => row.client?.hosting_site?.owner_organisation?.name, flex: 4 },
            { field: 'status', sortable: true, type: "singleSelect", valueOptions: Object.values(ApprovalStatus).map(status => t(status) + ""), headerName: t('Status'), flex: 3, valueGetter: (status) => t(status + ""), renderCell: (params: { row: AgreementResponse }) => (<ApprovalStatusChip {...getCurrentAgreement(agreements, params)} />) },
            { field: 'created_time', sortable: true, type: "dateTime", headerName: t('Requested'), flex: 3, valueGetter: (createdTime) => createdTime && new Date(createdTime), renderCell: (params: { row: AgreementResponse }) => DateToFormattedString(params.row.created_time) },
            { field: "dns_names", sortable: false, headerName: t('DNS names'), type: "string", valueGetter: (_, row: AgreementResponse) => [...new Set(row.service?.endpoints?.map(endpoint => endpoint.dns_name + "." + endpoint.dns_suffix?.dns_suffix + "." + getEnvironment().REACT_APP_DSDN_DOMAIN))], flex: 2 },
            { field: "service_ips", sortable: true, headerName: t('Service IP'), type: "string", valueGetter: (_, row: AgreementResponse) => [...new Set(row.service?.endpoints?.map(endpoint => endpoint.ip))], flex: 2, sortComparator: compareIpList },
            { field: "client_ips", sortable: true, headerName: t('Client IP'), type: "string", valueGetter: (_, row: AgreementResponse) => row.client_ip_addresses, flex: 2, sortComparator: compareIpList },
            { field: "provision_status", sortable: true, headerName: t('Provision status') + " v4", type: "string", valueGetter: (status) => t(status ?? ""), flex: 2 },
            { field: "provision_time", sortable: true, headerName: t('Provision time'), type: "dateTime", valueGetter: (provisionTime) => provisionTime && new Date(provisionTime), renderCell: (params: { row: AgreementResponse }) => DateToFormattedString(params.row.provision_time), flex: 3 },
            { field: "criticality_score", sortable: true, minWidth: 100, headerName: t('Criticality score'), valueGetter: (_, row: AgreementResponse) => row.service?.criticality_score, renderCell: (params: { row: AgreementResponse }) => <CriticalityScoreBadge ressource={params.row.service as ServiceBase} />, type: "number", flex: 1.5 },
        ];

        const buttonsView: GridColDef[] = [
            {
                field: 'events',
                sortable: false,
                hideable: false,
                type: "string",
                valueGetter: () => "",
                headerName: t('Actions'),
                minWidth: props.simpleView ? 100 : 220,
                renderCell: (params: any) => {
                    const agreement: AgreementResponse = getCurrentAgreement(agreements, params)
                    return (props.simpleView ?
                        <OpenInNewTabButton url={`/agreements/${agreement.uuid}`} />
                        :
                        <>
                            <InfoButton onClick={() => setClickedRow(agreement)} />
                            <Can I={PermissionEnum.AUDIT_READ} this={agreement}>
                                <OpenInNewTabButton tooltipText='Events' icon={<History />} url={`/events?entity_uuid=${agreement.uuid}`} />
                            </Can>
                            <OpenInNewTabButton url={`/agreements/${agreement.uuid}`} />
                        </>
                    )
                }
            }
        ]

        const listToShow = props.simpleView ?
            simpleView1.concat(simpleView2)
            :
            simpleView1.concat(advancedView1).concat(simpleView2).concat(advancedView2);
        return listToShow.concat(buttonsView);
    }

    const agreements = getAgreements(props);

    return (
        <>
            <DatagridWrapper
                dataGridId={props.dataGridId ?? DataGridId.AGREEMENTS}
                defaultPagesize={props.defaultPagesize}
                onRowClick={(row) => { setClickedRow(row.row) }}
                loading={agreements == undefined}
                rows={agreements ?? []}
                columns={getColumns(agreements ?? [])}
                initialSortModel={[{ field: 'created_time', sort: 'desc' }]}
                initialColumnVisibilityModel={{
                    dns_names: false,
                    service_ips: false,
                    client_ips: false,

                }}
            />

            <InfoDrawer
                open={clickedRow != undefined}
                onClose={() => setClickedRow(undefined)}
                entityUuid={clickedRow?.uuid}>
                {clickedRow &&
                    <AgreementInfoDialog
                        key={clickedRow.uuid}
                        agreement={clickedRow}
                        afterDeleteAction={() => setClickedRow(undefined)} />}
            </InfoDrawer>
        </>
    )

    function getAgreements(props: AgreementListProps) {
        if (props.agreements != undefined)
            return props.agreements


        const currentResult = props.getAgreements?.({
            pagination: new PaginationQueryPackage(),
            uuid: props.idToFetch,
            requestData: props.statusList
        });

        return currentResult?.data
    }
}

